import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ScrollToTop from "@/components/base/ScrollToTop";
import SchemaOrg from "@/components/base/SchemaOrg";
import { aboutInfo, testimonials } from "@/mocks/movies";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/d9muuosit35ii3jdlmg0";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("idle");
    setFormError("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setFormStatus("success");
      form.reset();
      return;
    }

    try {
      const res = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== "phone_alt")
            .map(([k, v]) => [k, String(v)])
        ).toString(),
      });

      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* not json */ }

      if (res.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const serverMsg = String(
          (parsed as Record<string, unknown>)?.meta?.message
          || (parsed as Record<string, unknown>)?.message
          || (parsed as Record<string, unknown>)?.meta?.detail
          || responseText
          || "提交失败，请稍后重试"
        );
        setFormError(serverMsg);
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请检查网络后重试");
      setFormStatus("error");
    }
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="AboutPage"
        data={{
          name: "关于八戒影院 - 品牌介绍与联系方式",
          description: "八戒影院品牌介绍、用户口碑与联系方式。了解八戒影院的品牌故事和发展历程。",
          url: "https://www.bajiecinema.com/about",
          isPartOf: { "@type": "WebSite", name: "八戒影院", url: "https://www.bajiecinema.com/" },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://www.bajiecinema.com/" },
            { "@type": "ListItem", position: 2, name: "关于我们" },
          ],
        }}
      />
      <Navbar />
      <main className="pt-20">
        <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "关于我们" }]} />

        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto">
            <div className="text-center mb-10">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                {aboutInfo.brand.title}
              </h1>
              <p className="text-sm text-foreground-500 max-w-2xl mx-auto">
                了解八戒影院的品牌故事与发展历程
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-14">
              <div className="rounded-lg overflow-hidden h-[320px] bg-background-100">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20movie%20theater%20interior%20with%20warm%20lighting%2C%20empty%20red%20velvet%20seats%2C%20elegant%20cinema%20atmosphere%2C%20soft%20golden%20glow%2C%20luxurious%20cinema%20design%2C%20wide%20angle%20shot%2C%20cinematic%20composition&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="八戒影院品牌故事"
                  title="八戒影院品牌故事"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="flex flex-col justify-center gap-4">
                {aboutInfo.brand.content.map((p, idx) => (
                  <p key={idx} className="text-sm text-foreground-600 leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                {aboutInfo.reputation.title}
              </h2>
              <p className="text-sm text-foreground-500">数据说话，品质保证</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-10">
              {aboutInfo.reputation.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-background-50 rounded-lg p-6 text-center border border-background-200/50"
                >
                  <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-2">
                    {stat.value}
                  </p>
                  <p className="text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="text-center mb-6">
              <h3 className="text-lg font-heading font-bold text-foreground-900 mb-6">用户评价</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="bg-background-50 rounded-lg p-5 border border-background-200/50"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                            <i
                              className={`text-xs ${
                                i < t.rating ? "ri-star-fill text-primary-500" : "ri-star-line text-foreground-300"
                              }`}
                            ></i>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
                  <p className="text-xs text-foreground-400 mt-3">{t.date}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                {aboutInfo.contact.title}
              </h2>
              <p className="text-sm text-foreground-500">有任何问题欢迎联系我们</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3 p-4 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100/80 text-primary-500 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</p>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100/80 text-accent-500 flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900 mb-1">微信</p>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100/80 text-secondary-500 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900 mb-1">公司地址</p>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 flex-shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900 mb-1">工作时间</p>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.workingHours}</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-6">
                <h3 className="text-base font-heading font-bold text-foreground-900 mb-4">发送消息</h3>
                <form className="flex flex-col gap-4" data-readdy-form onSubmit={handleSubmit}>
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <button
                    type="submit"
                    className="w-full py-2.5 bg-primary-500 text-background-50 font-semibold text-sm rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                  </button>
                  {formStatus === "success" && (
                    <p className="text-sm text-accent-600 text-center">消息已发送成功，我们会尽快回复您！</p>
                  )}
                  {formStatus === "error" && (
                    <p className="text-sm text-accent-600 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}